(() => {
  const sessionStorageKey = 'wob.workerSessionId';
  const queueStorageKey = 'wob.workerAnalyticsQueue';
  const eventIngestPath = '/interactions';
  const queueMaxAgeMs = 24 * 60 * 60 * 1000;
  const queueMaxAttempts = 8;
  const queueMaxEvents = 80;

  const normalizeEndpoint = (endpoint) => String(endpoint || '').trim().replace(/\/+$/, '');

  const normalizeText = (value) => String(value || '').trim();
  const trackedClickElements = new WeakSet();
  const endpointSelectors = [
    ['[data-collection-worker-endpoint]', 'collectionWorkerEndpoint'],
    ['[data-search-worker-endpoint]', 'searchWorkerEndpoint'],
    ['[data-header-search-endpoint]', 'headerSearchEndpoint'],
    ['[data-site-nav-products-endpoint]', 'siteNavProductsEndpoint'],
    ['[data-shop-the-category-worker-endpoint]', 'shopTheCategoryWorkerEndpoint'],
    ['[data-featured-worker-endpoint]', 'featuredWorkerEndpoint']
  ];

  const isDebugEnabled = () => {
    try {
      return new URLSearchParams(window.location.search).get('analytics_debug') === '1'
        || window.localStorage.getItem('wobAnalyticsDebug') === 'true';
    } catch (error) {
      return false;
    }
  };

  const debug = (...args) => {
    if (!isDebugEnabled()) return;

    console.info('[WOB analytics]', ...args);
  };

  const markTrackedClickElement = (element) => {
    trackedClickElements.add(element);
    window.setTimeout(() => {
      trackedClickElements.delete(element);
    }, 1500);
  };

  const getConfiguredEndpoint = () => normalizeEndpoint(window.WobAnalyticsConfig?.endpoint || '');

  const getEndpointForTarget = (target) => {
    for (const [selector, datasetKey] of endpointSelectors) {
      const scoped = target?.closest?.(selector);
      const scopedEndpoint = normalizeEndpoint(scoped?.dataset?.[datasetKey]);

      if (scopedEndpoint) return scopedEndpoint;
    }

    for (const [selector, datasetKey] of endpointSelectors) {
      const fallback = document.querySelector(selector);
      const fallbackEndpoint = normalizeEndpoint(fallback?.dataset?.[datasetKey]);

      if (fallbackEndpoint) return fallbackEndpoint;
    }

    return getConfiguredEndpoint();
  };

  const getEventId = () => {
    return window.crypto?.randomUUID?.() || `event-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const positiveInteger = (value) => {
    const parsed = Number.parseInt(value, 10);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  };

  const getShopifyClickSourceType = (event) => {
    if (normalizeText(event.sourcePlacement).startsWith('product-page')) return 'product_page';
    if (event.type === 'cart_product_clicked') return 'cart_drawer';
    if (event.type === 'search_suggestion_clicked') return 'search_dropdown';
    if (event.type === 'search_result_clicked') return 'search_results';
    if (event.type === 'collection_product_clicked' && event.source === 'collection') return 'collection_grid';
    if (event.type === 'collection_product_clicked' || event.type === 'menu_dropdown_product_clicked') {
      return 'recommendation';
    }

    return 'other';
  };

  const getShopifyClickSourceKey = (event, sourceType) => {
    if (sourceType === 'search_dropdown' || sourceType === 'search_results') {
      return normalizeText(event.query || event.normalizedQuery);
    }
    if (sourceType === 'collection_grid' || sourceType === 'recommendation') {
      return normalizeText(event.collectionHandle || event.sourceName || event.source);
    }
    if (sourceType === 'product_page') {
      return normalizeText(event.sourceName || event.sourcePlacement || event.source);
    }
    if (sourceType === 'cart_drawer') {
      return normalizeText(event.sourceName || event.sourcePlacement || 'Cart drawer');
    }

    return normalizeText(event.sourceName || event.sourcePlacement || event.source);
  };

  const publishShopifyProductClick = (detail = {}) => {
    const publish = window.Shopify?.analytics?.publish;

    if (typeof publish !== 'function') {
      debug('Shopify customer-event publisher unavailable');
      return false;
    }

    const sourceType = normalizeText(detail.source_type) || 'other';
    const payload = {
      schema_version: 1,
      click_id: normalizeText(detail.click_id) || getEventId(),
      product_id: normalizeText(detail.product_id),
      variant_id: normalizeText(detail.variant_id) || null,
      product_handle: normalizeText(detail.product_handle),
      source_type: sourceType,
      source_key: normalizeText(detail.source_key),
      result_set_id: normalizeText(detail.result_set_id) || null,
      collection_handle: normalizeText(detail.collection_handle) || null,
      placement: normalizeText(detail.placement) || null,
      position: positiveInteger(detail.position),
      worker_target: 'web'
    };

    if (!payload.product_id && !payload.product_handle) {
      debug('Shopify product click skipped without product identity', payload);
      return false;
    }

    try {
      publish.call(window.Shopify.analytics, 'wob:product_click', payload);
      debug('Shopify product click published', payload);
      return true;
    } catch (error) {
      debug('Shopify product click publish failed', error);
      return false;
    }
  };

  const publishTrackedProductClick = (event) => {
    if (event.resultType !== 'product') return false;

    const sourceType = getShopifyClickSourceType(event);

    return publishShopifyProductClick({
      click_id: event.eventId,
      product_id: event.productId,
      variant_id: event.variantId,
      product_handle: event.productHandle,
      source_type: sourceType,
      source_key: getShopifyClickSourceKey(event, sourceType),
      result_set_id: event.resultSetId,
      collection_handle: event.collectionHandle,
      placement: event.sourcePlacement,
      position: event.position
    });
  };

  const getSessionId = () => {
    try {
      const existing = window.localStorage.getItem(sessionStorageKey);

      if (existing) return existing;

      const next = window.crypto?.randomUUID?.() || `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      window.localStorage.setItem(sessionStorageKey, next);

      return next;
    } catch (error) {
      return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  };

  const isQueuedRecordUsable = (record, now = Date.now()) => {
    return Boolean(record?.endpoint && record?.event?.eventId)
      && Number(record.attempts || 0) < queueMaxAttempts
      && now - Number(record.queuedAt || 0) <= queueMaxAgeMs;
  };

  const readEventQueue = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(queueStorageKey) || '[]');
      const now = Date.now();

      return Array.isArray(parsed) ? parsed.filter((record) => isQueuedRecordUsable(record, now)) : [];
    } catch (error) {
      return [];
    }
  };

  const writeEventQueue = (queue) => {
    try {
      const nextQueue = queue.filter(isQueuedRecordUsable).slice(-queueMaxEvents);

      if (nextQueue.length) {
        window.localStorage.setItem(queueStorageKey, JSON.stringify(nextQueue));
      } else {
        window.localStorage.removeItem(queueStorageKey);
      }
    } catch (error) {
      debug('queue write failed', error);
    }
  };

  const queueEvent = (endpoint, event) => {
    if (!endpoint || !event?.eventId) return false;

    const queue = readEventQueue().filter((record) => record.event?.eventId !== event.eventId);
    const nextQueue = [
      ...queue,
      {
        endpoint,
        event,
        queuedAt: Date.now(),
        attempts: 0
      }
    ];

    writeEventQueue(nextQueue);
    debug('event queued', {
      eventId: event.eventId,
      type: event.type,
      queueLength: nextQueue.length
    });

    return true;
  };

  const removeQueuedEvents = (eventIds) => {
    const ids = new Set(eventIds.filter(Boolean));
    if (!ids.size) return;

    writeEventQueue(readEventQueue().filter((record) => !ids.has(record.event?.eventId)));
    debug('queue events flushed', Array.from(ids));
  };

  const markQueuedEventsAttempted = (eventIds) => {
    const ids = new Set(eventIds.filter(Boolean));
    if (!ids.size) return;

    writeEventQueue(readEventQueue().map((record) => {
      if (!ids.has(record.event?.eventId)) return record;

      return {
        ...record,
        attempts: Number(record.attempts || 0) + 1,
        lastAttemptAt: Date.now()
      };
    }));
  };

  const sendBeaconPayload = (url, body, eventIds = []) => {
    if (!navigator.sendBeacon) return false;

    const queued = navigator.sendBeacon(url, body);
    if (queued) debug('beacon queued', eventIds);

    return queued;
  };

  const sendEvents = ({ endpoint, events, keepalive = true, useFetch = true, onSuccess = null } = {}) => {
    const normalizedEndpoint = normalizeEndpoint(endpoint);
    const filteredEvents = Array.isArray(events) ? events.filter(Boolean) : [];

    if (!normalizedEndpoint || !filteredEvents.length) return false;

    const body = JSON.stringify({ events: filteredEvents });
    const url = `${normalizedEndpoint}${eventIngestPath}`;
    const fetchTransport = window.__wobNativeFetch || (window.fetch ? window.fetch.bind(window) : null);

    if (useFetch && fetchTransport) {
      try {
        fetchTransport(url, {
          method: 'POST',
          body,
          keepalive,
          mode: 'no-cors',
          credentials: 'omit',
          cache: 'no-store'
        }).then(() => {
          debug('fetch transport resolved', filteredEvents.map((event) => event.eventId));
          if (typeof onSuccess === 'function') onSuccess();
        }).catch((error) => {
          debug('event fetch failed', error);
        });

        return true;
      } catch (error) {
        debug('event fetch could not start', error);
      }
    }

    const beaconQueued = sendBeaconPayload(url, body, filteredEvents.map((event) => event.eventId));

    if (beaconQueued && typeof onSuccess === 'function') onSuccess();

    return beaconQueued;
  };

  const flushQueuedEvents = () => {
    const queue = readEventQueue();

    if (!queue.length) {
      debug('queue empty');
      return false;
    }

    queue.forEach((record) => {
      const endpoint = normalizeEndpoint(record.endpoint);
      const event = record.event;
      const eventId = event?.eventId || '';

      if (!endpoint || !eventId) return;

      markQueuedEventsAttempted([eventId]);
      debug('flushing queued events', {
        endpoint,
        eventIds: [eventId]
      });

      sendEvents({
        endpoint,
        events: [event],
        keepalive: false,
        useFetch: true,
        onSuccess: () => removeQueuedEvents([eventId])
      });
    });

    return true;
  };

  const track = ({ endpoint, eventType, source, context = {}, link = null } = {}) => {
    const normalizedEndpoint = normalizeEndpoint(endpoint);

    if (!eventType) {
      debug('skipped event', { eventType, endpoint });
      return false;
    }

    const event = {
      type: eventType,
      eventId: getEventId(),
      timestamp: new Date().toISOString(),
      sessionId: getSessionId(),
      source,
      page: {
        path: window.location.pathname,
        url: window.location.href
      },
      ...context
    };
    const isNavigationClick = link instanceof HTMLAnchorElement;
    const shopifyPublished = publishTrackedProductClick(event);

    if (!normalizedEndpoint) {
      debug('worker event skipped without endpoint', { eventType, shopifyPublished });
      return shopifyPublished;
    }

    debug('tracking event', event);
    queueEvent(normalizedEndpoint, event);

    return sendEvents({
      endpoint: normalizedEndpoint,
      events: [event],
      useFetch: !isNavigationClick,
      onSuccess: () => removeQueuedEvents([event.eventId])
    });
  };

  const getSearchQuery = (root) => {
    const input = root?.querySelector?.('[data-header-search-input], [data-search-input]');
    const rootQuery = normalizeText(root?.dataset?.searchQuery);
    const inputQuery = normalizeText(input?.value);
    const urlQuery = normalizeText(new URLSearchParams(window.location.search).get('q'));

    return inputQuery || rootQuery || urlQuery;
  };

  const getProductHandleFromUrl = (value) => {
    try {
      const url = new URL(value || '', window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);

      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  };

  const getProductCardPosition = (card) => {
    return Number(card?.dataset?.collectionResultPosition || card?.dataset?.searchResultPosition || 0);
  };

  const getCollectionSourceName = (root) => {
    return normalizeText(root?.dataset?.collectionTitle)
      || normalizeText(root?.dataset?.collectionHandle)
      || 'Collection';
  };

  const trackHeaderSearchClick = (event, target) => {
    const resultLink = target.closest('[data-header-search-result-link]');
    const form = resultLink?.closest('[data-header-search]');

    if (!resultLink || !form) return false;
    if ((resultLink.dataset.headerSearchResultType || '') !== 'product') {
      debug('ignored header non-product result', resultLink.dataset.headerSearchResultType || '');
      return true;
    }
    if (trackedClickElements.has(resultLink)) return true;

    markTrackedClickElement(resultLink);

    const query = normalizeText(resultLink.dataset.headerSearchQuery) || getSearchQuery(form);

    track({
      endpoint: form.dataset.headerSearchEndpoint,
      eventType: 'search_suggestion_clicked',
      source: 'header-search',
      link: resultLink,
      context: {
        query,
        normalizedQuery: query.toLowerCase(),
        resultType: 'product',
        sourcePlacement: 'search-dropdown',
        sourceName: 'Header search',
        productId: resultLink.dataset.headerSearchProductId || '',
        productHandle: resultLink.dataset.headerSearchProductHandle || '',
        productTitle: resultLink.dataset.headerSearchProductTitle || '',
        position: Number(resultLink.dataset.headerSearchResultPosition || 0)
      }
    });

    return true;
  };

  const trackSearchResultClick = (event, target) => {
    const root = target.closest('[data-worker-search]');
    const link = target.closest('[data-search-result-link], [data-product-card-link]');
    const productCard = link?.closest('[data-search-product-result], .product-card');

    if (!root || !link || !productCard) {
      if (root) {
        debug('search click did not match product card', {
          hasRoot: Boolean(root),
          hasLink: Boolean(link),
          hasProductCard: Boolean(productCard)
        });
      }
      return false;
    }
    if (trackedClickElements.has(link)) return true;

    markTrackedClickElement(link);

    const query = getSearchQuery(root);
    const resultSetId = normalizeText(root.dataset.searchResultSetId);

    track({
      endpoint: root.dataset.searchWorkerEndpoint,
      eventType: 'search_result_clicked',
      source: 'search',
      link,
      context: {
        query,
        normalizedQuery: query.toLowerCase(),
        resultSetId,
        resultType: 'product',
        sourcePlacement: 'search-page',
        sourceName: 'Search results',
        productId: productCard.dataset.productId || '',
        productHandle: productCard.dataset.productHandle || '',
        productTitle: productCard.dataset.productTitle || '',
        position: Number(productCard.dataset.searchResultPosition || productCard.dataset.collectionResultPosition || 0)
      }
    });

    return true;
  };

  const trackCollectionProductClick = (event, target) => {
    const root = target.closest('[data-collection-infinite-scroll]');
    const link = target.closest('[data-product-card-link]');
    const productCard = link?.closest('.product-card');

    if (!root || !link || !productCard || !productCard.dataset.productId) {
      if (root || link) {
        debug('collection click did not match product card', {
          hasRoot: Boolean(root),
          hasLink: Boolean(link),
          hasProductCard: Boolean(productCard),
          productId: productCard?.dataset?.productId || ''
        });
      }
      return false;
    }
    if (trackedClickElements.has(link)) return true;

    markTrackedClickElement(link);

    track({
      endpoint: root.dataset.collectionWorkerEndpoint,
      eventType: 'collection_product_clicked',
      source: 'collection',
      link,
      context: {
        resultType: 'product',
        sourcePlacement: 'collection',
        sourceName: getCollectionSourceName(root),
        collectionId: root.dataset.collectionId || '',
        collectionHandle: root.dataset.collectionHandle || '',
        productId: productCard.dataset.productId || '',
        productHandle: productCard.dataset.productHandle || '',
        productTitle: productCard.dataset.productTitle || '',
        position: Number(productCard.dataset.collectionResultPosition || 0),
        sortKey: new URLSearchParams(window.location.search).get('sort_by') || root.dataset.collectionSortKey || 'recommended',
        workerRendered: productCard.dataset.collectionWorkerProduct === 'true'
      }
    });

    return true;
  };

  const trackMenuDropdownProductClick = (event, target) => {
    const card = target.closest('[data-site-nav-product-card], .site-nav__dropdown-products a[href*="/products/"]');
    const link = card?.matches('a[href]') ? card : card?.closest('a[href]') || card?.querySelector?.('a[href]');
    const rail = card?.closest('[data-site-nav-products], .site-nav__dropdown-products');

    if (!card || !link || !rail) return false;
    if (trackedClickElements.has(link)) return true;

    markTrackedClickElement(link);

    const analyticsSource = rail.dataset.siteNavProductsAnalyticsSource || 'header-nav-dropdown';
    const sourceGroup = rail.dataset.siteNavProductsSourceGroup || 'header';
    const collectionTitle = normalizeText(rail.dataset.siteNavProductsCollectionTitle)
      || normalizeText(rail.dataset.siteNavProductsCollectionHandle);

    track({
      endpoint: rail.dataset.siteNavProductsEndpoint || getEndpointForTarget(link),
      eventType: 'menu_dropdown_product_clicked',
      source: analyticsSource,
      link,
      context: {
        resultType: 'product',
        sourcePlacement: 'menu-dropdown',
        sourceName: collectionTitle ? `Header navigation - ${collectionTitle}` : 'Header navigation',
        sourceGroup,
        collectionId: rail.dataset.siteNavProductsCollectionId || '',
        collectionHandle: rail.dataset.siteNavProductsCollectionHandle || '',
        productId: card.dataset.siteNavProductId || link.dataset.siteNavProductId || '',
        numericProductId: card.dataset.siteNavProductNumericId || link.dataset.siteNavProductNumericId || '',
        productHandle: card.dataset.siteNavProductHandle || link.dataset.siteNavProductHandle || getProductHandleFromUrl(link.getAttribute('href')),
        productTitle: card.dataset.siteNavProductTitle || link.dataset.siteNavProductTitle || normalizeText(card.querySelector('.site-nav__product-title')?.textContent),
        position: Number(card.dataset.siteNavProductPosition || link.dataset.siteNavProductPosition || 0),
        workerRendered: (card.dataset.siteNavProductSource || link.dataset.siteNavProductSource) === 'worker'
      }
    });

    return true;
  };

  const trackProductShopTheLookClick = (event, target) => {
    const root = target.closest('[data-product-shop-the-look]');
    const link = target.closest('[data-product-card-link]');
    const productCard = link?.closest('.product-card');

    if (!root || !link || !productCard || !productCard.dataset.productId) return false;
    if (trackedClickElements.has(link)) return true;

    markTrackedClickElement(link);
    track({
      endpoint: getEndpointForTarget(link),
      eventType: 'collection_product_clicked',
      source: 'product-page-shop-the-look',
      link,
      context: {
        resultType: 'product',
        sourcePlacement: 'product-page-shop-the-look',
        sourceName: root.dataset.productShopTheLookSourceName || 'Shop the Look',
        collectionHandle: root.dataset.productShopTheLookCollectionHandle || '',
        productId: productCard.dataset.productId || '',
        productHandle: productCard.dataset.productHandle || '',
        productTitle: productCard.dataset.productTitle || '',
        position: getProductCardPosition(productCard)
      }
    });

    return true;
  };

  const trackCartDrawerProductClick = (event, target) => {
    const link = target.closest('[data-cart-product-link]');
    const item = link?.closest('[data-cart-drawer-item]');

    if (!link || !item || trackedClickElements.has(link)) return false;

    const productId = normalizeText(item.dataset.productId);
    const productHandle = normalizeText(item.dataset.productHandle)
      || getProductHandleFromUrl(link.getAttribute('href'));
    if (!productId && !productHandle) return false;

    markTrackedClickElement(link);
    track({
      // Cart navigation belongs in durable click-to-purchase attribution, not
      // the Worker's collection/search engagement buckets.
      endpoint: '',
      eventType: 'cart_product_clicked',
      source: 'cart-drawer',
      link,
      context: {
        resultType: 'product',
        sourcePlacement: 'cart-drawer-item',
        sourceName: 'Existing cart item',
        productId,
        variantId: item.dataset.variantId || '',
        productHandle,
        productTitle: item.dataset.productTitle || '',
        position: positiveInteger(item.dataset.lineIndex)
      }
    });

    return true;
  };

  const getQuickAddTracking = (form, productCard) => {
    const baseContext = {
      resultType: 'product',
      interaction: 'quick-add',
      productId: productCard.dataset.productId || '',
      variantId: form.querySelector('[data-product-card-variant-input]')?.value || '',
      productHandle: productCard.dataset.productHandle || '',
      productTitle: productCard.dataset.productTitle || '',
      position: getProductCardPosition(productCard)
    };
    const shopTheLook = productCard.closest('[data-product-shop-the-look]');
    if (shopTheLook) {
      return {
        endpoint: getEndpointForTarget(form),
        eventType: 'collection_product_clicked',
        source: 'product-page-shop-the-look',
        context: {
          ...baseContext,
          sourcePlacement: 'product-page-shop-the-look-quick-add',
          sourceName: `${shopTheLook.dataset.productShopTheLookSourceName || 'Shop the Look'} quick add`,
          collectionHandle: shopTheLook.dataset.productShopTheLookCollectionHandle || ''
        }
      };
    }

    const searchRoot = productCard.closest('[data-worker-search]');
    if (searchRoot) {
      const query = getSearchQuery(searchRoot);
      return {
        endpoint: searchRoot.dataset.searchWorkerEndpoint,
        eventType: 'search_result_clicked',
        source: 'search',
        context: {
          ...baseContext,
          query,
          normalizedQuery: query.toLowerCase(),
          resultSetId: normalizeText(searchRoot.dataset.searchResultSetId),
          sourcePlacement: 'search-page-quick-add',
          sourceName: 'Search results quick add'
        }
      };
    }

    const featuredRoot = productCard.closest('[data-featured-collection-tabs]');
    if (featuredRoot) {
      const panel = productCard.closest('[data-featured-worker-panel]');
      const collectionTitle = normalizeText(panel?.dataset?.featuredCollectionTitle);
      return {
        endpoint: featuredRoot.dataset.featuredWorkerEndpoint,
        eventType: 'collection_product_clicked',
        source: 'homepage-featured-collection',
        context: {
          ...baseContext,
          sourcePlacement: 'homepage-featured-collection-quick-add',
          sourceName: collectionTitle || normalizeText(featuredRoot.querySelector('.featured-collection__header h2')?.textContent),
          collectionHandle: panel?.dataset?.featuredCollectionHandle || '',
          collectionTitle
        }
      };
    }

    const collectionRoot = productCard.closest('[data-collection-infinite-scroll]');
    if (collectionRoot) {
      return {
        endpoint: collectionRoot.dataset.collectionWorkerEndpoint,
        eventType: 'collection_product_clicked',
        source: 'collection',
        context: {
          ...baseContext,
          sourcePlacement: 'collection-quick-add',
          sourceName: getCollectionSourceName(collectionRoot),
          collectionHandle: collectionRoot.dataset.collectionHandle || ''
        }
      };
    }

    if (document.body.classList.contains('template-product')) {
      return {
        endpoint: getEndpointForTarget(form),
        eventType: 'collection_product_clicked',
        source: 'product-page-quick-add',
        context: {
          ...baseContext,
          sourcePlacement: 'product-page-quick-add',
          sourceName: 'Product page quick add'
        }
      };
    }

    return null;
  };

  const trackProductCardQuickAddSubmit = (event) => {
    const form = event.target instanceof HTMLFormElement
      ? event.target.closest('[data-cart-add-form]')
      : null;
    const productCard = form?.closest('.product-card');

    if (!form || !productCard || trackedClickElements.has(form)) return;

    const tracking = getQuickAddTracking(form, productCard);
    if (!tracking) return;

    markTrackedClickElement(form);
    track(tracking);
  };

  const trackFeaturedCollectionProductClick = (event, target) => {
    const root = target.closest('[data-featured-collection-tabs]');
    const link = target.closest('[data-product-card-link]');
    const productCard = link?.closest('.product-card');
    const panel = productCard?.closest('[data-featured-worker-panel]');

    if (!root || !link || !productCard || !panel || !productCard.dataset.productId) return false;
    if (trackedClickElements.has(link)) return true;

    markTrackedClickElement(link);
    const collectionTitle = normalizeText(panel.dataset.featuredCollectionTitle);
    track({
      endpoint: root.dataset.featuredWorkerEndpoint,
      eventType: 'collection_product_clicked',
      source: 'homepage-featured-collection',
      link,
      context: {
        resultType: 'product',
        sourcePlacement: 'homepage-featured-collection',
        sourceName: collectionTitle || normalizeText(root.querySelector('.featured-collection__header h2')?.textContent),
        collectionId: panel.dataset.featuredCollectionId || '',
        collectionHandle: panel.dataset.featuredCollectionHandle || '',
        collectionTitle,
        productId: productCard.dataset.productId || '',
        productHandle: productCard.dataset.productHandle || '',
        productTitle: productCard.dataset.productTitle || '',
        position: Number(productCard.dataset.featuredResultPosition || 0),
        sortKey: panel.dataset.featuredSortKey || 'recommended',
        workerRendered: productCard.dataset.featuredWorkerProduct === 'true'
      }
    });

    return true;
  };

  const trackShopTheCategoryProductClick = (event, target) => {
    const root = target.closest('[data-shop-the-category]');
    const link = target.closest('[data-shop-the-category-product-card]');

    if (!root || !link) return false;
    if (trackedClickElements.has(link)) return true;

    const productId = normalizeText(link.dataset.productId);
    const productHandle = normalizeText(link.dataset.productHandle)
      || getProductHandleFromUrl(link.getAttribute('href'));
    if (!productId && !productHandle) return false;

    markTrackedClickElement(link);
    const collectionTitle = normalizeText(root.dataset.shopTheCategoryCollectionTitle);
    track({
      endpoint: root.dataset.shopTheCategoryWorkerEndpoint,
      eventType: 'collection_product_clicked',
      source: 'homepage-shop-by-category',
      link,
      context: {
        resultType: 'product',
        sourcePlacement: 'homepage-shop-by-category',
        sourceName: collectionTitle || 'Shop by Category',
        collectionId: root.dataset.shopTheCategoryCollectionId || '',
        collectionHandle: root.dataset.shopTheCategoryCollectionHandle || '',
        collectionTitle,
        productId,
        productHandle,
        productTitle: link.dataset.productTitle || '',
        position: Number(link.dataset.shopTheCategoryResultPosition || 0),
        sortKey: root.dataset.shopTheCategorySortKey || 'recommended',
        workerRendered: true
      }
    });

    return true;
  };

  const trackProductInteraction = (event) => {
    const target = event.target instanceof Element ? event.target : null;

    if (!target) return;
    if (trackCartDrawerProductClick(event, target)) return;
    if (trackHeaderSearchClick(event, target)) return;
    if (trackMenuDropdownProductClick(event, target)) return;
    if (trackSearchResultClick(event, target)) return;
    if (trackProductShopTheLookClick(event, target)) return;
    if (trackShopTheCategoryProductClick(event, target)) return;
    if (trackFeaturedCollectionProductClick(event, target)) return;
    trackCollectionProductClick(event, target);
  };

  document.addEventListener('click', trackProductInteraction, true);
  document.addEventListener('submit', trackProductCardQuickAddSubmit, true);

  flushQueuedEvents();
  debug('initialized');

  window.WobWorkerAnalytics = {
    flush: flushQueuedEvents,
    getSessionId,
    getEndpointForTarget,
    normalizeEndpoint,
    track
  };
  window.WobProductAnalytics = {
    publishClick: publishShopifyProductClick
  };
})();
